import useChartYMultiplier from 'hooks/shared/chart/useChartYMultiplier';
import useAppTheme from 'hooks/shared/useAppTheme';
import useCurrencyText from 'hooks/shared/useCurrencyText';
import moment from 'moment';
import numeral from 'numeral';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

interface Props {
  chart: MultiItemsChart;
  currency: Currency;
}

const StrategyIncomeChart: FC<Props> = ({ chart, currency }) => {
  const theme = useAppTheme();

  const currencyFormatter = useCurrencyText();

  const chartMultiplier = useChartYMultiplier(chart.settings);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chart.points}>
        <Tooltip
          formatter={(value, name: string) => {
            const splittedName = name.split('.');
            value = chartMultiplier(+value);

            return [
              currencyFormatter(value, { symbol: currency.symbol }),
              splittedName[splittedName.length - 1],
            ];
          }}
          labelFormatter={(date) => moment(date).format('ll')}
        />
        <XAxis
          tickLine={false}
          dataKey="date"
          axisLine={{ stroke: theme.colors[Color.Shade100], strokeWidth: 2 }}
          tickFormatter={(value) => moment(value).format('YYYY')}
        />
        <YAxis
          axisLine={false}
          orientation="right"
          scale="linear"
          tickLine={false}
          tickSize={0}
          width={75}
          tickMargin={10}
          ticks={[20, 40, 60, 80, 100]}
          tickFormatter={(value, index) => {
            value = chartMultiplier(value);

            return numeral(value).format('0.0a');
          }}
          domain={([minY, maxY]) => [minY, maxY]}
        />
        <defs>
          {Object.keys(chart.data).map((key) => {
            return (
              <linearGradient key={key} id={key} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={chart.data[key].color}
                  stopOpacity={0.4}
                />
                <stop
                  offset="95%"
                  stopColor={chart.data[key].color}
                  stopOpacity={0}
                />
              </linearGradient>
            );
          })}
        </defs>
        {Object.keys(chart.data).map((key) => {
          return (
            <Area
              key={key}
              dataKey={`prices.${key}`}
              stroke={chart.data[key].color}
              strokeWidth={3}
              dot={false}
              fill={`url(#${key})`}
              fillOpacity={chart.data[key].isFilled ? 1 : 0}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default StrategyIncomeChart;
